import { AbstractControl, FormArray, FormControl, FormGroup, ValidationErrors, ValidatorFn } from "@angular/forms";
import { parsePhoneNumber } from "libphonenumber-js";
import { concat, Observable, of } from "rxjs";

export function throwExpression(error: Error): never {
    throw error;
}

export function notNullOrUndefined<T>(value: T | null | undefined): value is T {
    return value !== null && value !== undefined;
}

export function nullOrUndefined<T>(value: T | null | undefined): value is null | undefined {
    return value === null || value === undefined;
}

type WithoutNullOrUndefined<T> = Exclude<T, undefined | null>;

type ModelToFormItem<T> =
    WithoutNullOrUndefined<T> extends Array<infer U> ? (FormArray<ModelToFormItem<U>> | FormControl<T>) :
    WithoutNullOrUndefined<T> extends object ? FormGroup<ModelToFormGroup<WithoutNullOrUndefined<T>>> :
    FormControl<T>;

export type ModelToFormGroup<T extends object> = { [key in keyof Required<T>]: ModelToFormItem<T[key]> };

export function EmptyFormArray<T extends AbstractControl>(): FormArray<T> {
    return new FormArray<T>([]);
}

type RequireKeys<T extends Record<string, unknown>, K extends keyof T> = Pick<{ [key in K]-?: NonNullable<T[key]> }, K>;

export function hasRequiredProperties<T extends Record<string | symbol | number, unknown>, K extends keyof T>(requiredKeys: K[]) {
    return (obj: T): obj is T & RequireKeys<T, K> => Object.keys(obj).filter(key => key in requiredKeys).every(key => obj[key] !== null && obj[key] !== undefined);
}

export const phoneValidator: ValidatorFn = (control: AbstractControl<string | null | undefined>): ValidationErrors | null => {
    if (nullOrUndefined(control.value)) {
        return null;
    }

    try {
        const result = parsePhoneNumber(control.value, 'NL');

        if (result.isValid()) {
            return null;
        }
    } catch (e: unknown) {
        return { 'phone': {} };
    }

    return { 'phone': {} };
};

export function loadingObservable<T>(observable: Observable<T>) {
    return concat(
        of({ loading: true }),
        observable,
        of({ loading: false })
    )
}
